import { FC, MouseEvent, useState } from 'react';
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	OutlinedInput,
	Popover,
	Stack,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Add, Check } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getNightOrDayPalette } from '../utils/getNightOrDayPalette';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';
import useDeepCompareEffect from 'use-deep-compare-effect';

type AddItemSelectProps = Readonly<{
	label: string;
	isLoading: boolean;
	options: readonly { id: string; label: string }[];
	noItemsText: string;
	createText: string;
	emptyItemErrorText: string;
	createDialogTitle: string;
	defaultValue: readonly string[];
	onChange: (value: readonly string[]) => void;
	onCreate: (value: string, onSuccess: (value: string) => void) => void;
	onRedirect?: () => void;
}>;

const createFormSchema = (emptyItemErrorMessage: string) =>
	z.object({
		item: z.string().nonempty(emptyItemErrorMessage),
	});

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

export const ItemMultiSelect: FC<AddItemSelectProps> = ({
	label,
	isLoading,
	options,
	noItemsText,
	createText,
	defaultValue,
	onChange,
	onCreate,
	onRedirect,
	emptyItemErrorText,
	createDialogTitle,
}) => {
	const theme = useTheme();

	const form = useTypedForm({
		schema: createFormSchema(emptyItemErrorText),
	});

	const [value, setValue] = useState(defaultValue);

	useDeepCompareEffect(() => {
		setValue(defaultValue);
	}, [setValue, defaultValue]);

	const [isCreatingItem, setIsCreatingItem] = useState(false);

	const noAvailableOptions = !isLoading && (options.length || 0) === 0;

	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [addItemAnchorEl, setAddItemAnchorEl] = useState<null | HTMLElement>(null);

	const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
		if (!isLoading) {
			setMenuAnchorEl(event.currentTarget);
		}
	};

	const handleAddItemClick = (event: MouseEvent<HTMLElement>) => {
		if (onRedirect !== undefined) {
			onRedirect();
			return;
		}
		setAddItemAnchorEl(event.currentTarget);
	};

	const handleCreateItem = ({ item }: FormValues) => {
		setIsCreatingItem(true);
		onCreate(item, (newItem) => {
			setIsCreatingItem(false);
			setAddItemAnchorEl(null);
			form.reset();
			setValue((previousValue) => {
				const newValue = [...previousValue, newItem];
				onChange(newValue);
				return newValue;
			});
		});
	};

	return (
		<>
			<Button
				variant={'text'}
				color={'secondary'}
				startIcon={<Add />}
				sx={{
					position: 'relative',
					left: '-6px',
					height: 28,
					justifyContent: 'start',
					textTransform: 'none',
				}}
				onClick={handleSelectClick}
			>
				{label}
			</Button>
			<Menu anchorEl={menuAnchorEl} open={menuAnchorEl != null} onClose={() => setMenuAnchorEl(null)}>
				{options.map(({ id, label }) => (
					<MenuItem
						key={id}
						sx={{
							gap: 1,
							margin: 0.5,
							borderRadius: 0.5,
						}}
						onClick={() => {
							setValue((previousValue) => {
								const newValue = previousValue.includes(id) ? previousValue.filter((value) => value !== id) : [...previousValue, id];
								onChange(newValue);
								return newValue;
							});
						}}
					>
						<ListItemIcon>{value.includes(id) && <Check />}</ListItemIcon>
						<ListItemText>
							<Box sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
						</ListItemText>
					</MenuItem>
				))}
				{noAvailableOptions && (
					<MenuItem
						disabled={true}
						sx={{
							gap: 1,
							margin: 0.5,
							borderRadius: 0.5,
							fontStyle: 'italic',
						}}
					>
						{noItemsText}
					</MenuItem>
				)}
				<MenuItem
					sx={{
						backgroundColor: theme.palette.geekblue[0],
						color: theme.palette.primary[400],
						gap: 1,
						margin: 0.5,
						borderRadius: 0.5,
					}}
					onClick={handleAddItemClick}
				>
					<Stack direction={'row'} gap={1}>
						<ListItemIcon>
							<AddIcon sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][900] }} />
						</ListItemIcon>
						<ListItemText>
							<Typography color={(theme) => theme.palette[getNightOrDayPalette(theme)][900]}>{createText}</Typography>
						</ListItemText>
					</Stack>
				</MenuItem>
			</Menu>
			<Popover
				anchorEl={addItemAnchorEl}
				open={addItemAnchorEl != null}
				onClose={() => setAddItemAnchorEl(null)}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<form
					noValidate
					onSubmit={(event) => {
						form.handleSubmit(handleCreateItem)(event);
						event.stopPropagation();
					}}
				>
					<DialogTitle sx={{ padding: 2, pb: 0 }}>{createDialogTitle}</DialogTitle>
					<DialogContent sx={{ paddingX: 2, paddingBottom: 1 }}>
						<OutlinedInput {...form.register('item')} autoFocus sx={{ mt: 2 }} />
					</DialogContent>
					<DialogActions sx={{ paddingX: 2, pb: 2 }}>
						<Button variant={'contained'} type={'submit'} disabled={isCreatingItem}>
							<FormattedMessage id={'common.create'} />
						</Button>
					</DialogActions>
				</form>
			</Popover>
		</>
	);
};
